import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { useTaal } from "@/contexts/TaalContext";
import { WORKERBRONNEN, zinUitFormulier } from "@/lib/slashopdrachten";
import { DAGEN_KORT } from "@/lib/workerplanning";

/**
 * Slash-opdrachten deel 2 (24 okt 2026): /worker en /agent zonder tekst openen dit formulier.
 * Het maakt NIETS aan: het stelt de zin samen en stuurt die als bericht, en dan volgt de
 * bestaande voorstelkaart met Bevestig. /agent toont geen vast tijdstip.
 */
export default function SlashWorkerFormulier({ soort, onOpenChange, onStuur }) {
  const { t, taal } = useTaal();
  const [velden, setVelden] = useState({ wat: "", naam: "", bronnen: [], dagen: [], tijd: "08:00" });
  useEffect(() => { if (soort) setVelden({ wat: "", naam: "", bronnen: [], dagen: [], tijd: "08:00" }); }, [soort]);
  const zin = soort ? zinUitFormulier(soort, velden, taal) : "";
  const wissel = (veld, waarde) => setVelden((v) => ({
    ...v, [veld]: v[veld].includes(waarde) ? v[veld].filter((x) => x !== waarde) : [...v[veld], waarde] }));
  return (
    <Dialog open={Boolean(soort)} onOpenChange={(open) => { if (!open) onOpenChange?.(null); }}>
      <DialogContent className="sm:max-w-[520px]" data-testid="slash-worker-formulier">
        <DialogHeader>
          <DialogTitle>{soort === "agent" ? t("Nieuwe agent") : t("Nieuwe digitale worker")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="slash-wat">{t("Wat moet hij doen?")}</Label>
            <Input id="slash-wat" value={velden.wat} data-testid="slash-wat"
              placeholder={t("vat mijn TASQ-taken samen")}
              onChange={(e) => { const w = e.target.value; setVelden((v) => ({ ...v, wat: w })); }} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="slash-naam">{t("Korte naam (mag leeg)")}</Label>
            <Input id="slash-naam" value={velden.naam} data-testid="slash-naam"
              onChange={(e) => { const w = e.target.value; setVelden((v) => ({ ...v, naam: w })); }} />
          </div>
          <div className="space-y-1">
            <Label>{t("Mag lezen")}</Label>
            <div className="flex flex-wrap gap-3">
              {WORKERBRONNEN.map((b) => (
                <label key={b.id} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={velden.bronnen.includes(b.id)} onCheckedChange={() => wissel("bronnen", b.id)}
                    aria-label={b.woord} data-testid={`slash-bron-${b.id}`} />
                  <span>{b.woord}</span>
                </label>
              ))}
            </div>
          </div>
          {(soort === "worker" || soort === "agent") && (
            <div className="space-y-1">
              <Label>{t("Vast tijdstip")}</Label>
              <div className="flex flex-wrap items-center gap-2">
                {DAGEN_KORT[taal === "en" ? "en" : "nl"].map((naam, d) => (
                  <Button key={naam} type="button" size="sm" variant={velden.dagen.includes(d) ? "default" : "outline"}
                    aria-pressed={velden.dagen.includes(d)} onClick={() => wissel("dagen", d)} data-testid={`slash-dag-${d}`}>
                    {naam}
                  </Button>
                ))}
                <Input type="time" className="w-28" aria-label={t("Tijd")} value={velden.tijd} data-testid="slash-tijd"
                  onChange={(e) => { const w = e.target.value; setVelden((v) => ({ ...v, tijd: w })); }} />
              </div>
              <p className="text-xs text-muted-foreground">{t("Zonder dag draait hij alleen als je hem start.")}</p>
            </div>
          )}
          {zin && <p className="text-xs text-muted-foreground break-words" data-testid="slash-zin">{zin}</p>}
          <Button type="button" disabled={!zin} data-testid="slash-worker-stuur"
            onClick={() => { onStuur?.(zin); onOpenChange?.(null); }}>
            {t("Voorstel maken")}
          </Button>
          <p className="text-xs text-muted-foreground">
            {t("Er wordt nog niets aangemaakt: je krijgt eerst een voorstelkaart met Bevestig.")}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
